import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useParams } from "react-router-dom";
import { useTheme } from "../../../shared/common/ThemeContext";
import { fetchAuth } from "../../../shared/common/api";

import ImageryTab from "../../../shared/staff/ImageryTab";
import InjuryTab from "../../../shared/staff/InjuryTab";
import NotesTab from "../../../shared/staff/NotesTab";
import BiomarkersTab from "../../../shared/staff/BiomarkersTab";
import { smartDecode } from "../../../shared/common/hashid";

import { useMe } from "../../../shared/common/useMe";

const TABS = { FILES: "files", INJURY: "injury", NOTES: "notes", BIOMARKERS: "biomarkers" };

const TabButton = ({ active, onClick, children, disabled }) => {
  const { isDarkMode } = useTheme();
  const base = "px-4 py-2 rounded-xl text-sm font-semibold transition-colors border focus:outline-none";
  const activeCls = "bg-indigo-600 border-indigo-500 text-white";
  const inactiveCls = isDarkMode
    ? "bg-gray-800/60 border-gray-700 text-gray-200 hover:bg-gray-700"
    : "bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200";
  const disabledCls = "opacity-60 cursor-not-allowed hover:bg-inherit";
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${base} ${active ? activeCls : inactiveCls} ${disabled ? disabledCls : ""}`}
    >
      {children}
    </button>
  );
};
TabButton.propTypes = { active: PropTypes.bool, onClick: PropTypes.func, children: PropTypes.node, disabled: PropTypes.bool };

export default function PlayerUpdateInfosPage() {
  const { playerId: encodedPlayerId } = useParams();
  const playerId = smartDecode(encodedPlayerId) ?? encodedPlayerId;

  const { isDarkMode } = useTheme();

  const [player, setPlayer] = useState(null);
  const [loadingPlayer, setLoadingPlayer] = useState(true);

  const [activeTab, setActiveTab] = useState(TABS.FILES);

  // Permissions via JWT
  const { me, loading: loadingPerms } = useMe();
  const roles = me?.roles || [];
  const hasRole = (r) => roles.includes(r);
  const isAdmin = hasRole("admin");

  // Règles d’accès
  const canSeeCore = isAdmin || hasRole("doctor"); // Imagery + Injury + Biomarkers
  const canSeeNotes = isAdmin || hasRole("physiotherapist"); // Notes uniquement
  useEffect(() => {
    let alive = true;

    (async () => {
      if (!playerId) {
        if (alive) {
          setPlayer(null);
          setLoadingPlayer(false);
        }
        return;
      }

      try {
        setLoadingPlayer(true);
        const res = await fetchAuth(`/players/${playerId}`);
        const data = await res.json().catch(() => null);
        if (!res.ok) throw new Error(data?.error || "player_fetch_failed");
        if (alive) setPlayer(data || null);
      } catch (e) {
        if (alive) setPlayer(null);
      } finally {
        if (alive) setLoadingPlayer(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [playerId]);
  // Liste des onglets autorisés, dans un ordre préféré
  const allowedTabs = useMemo(() => {
    const list = [];
    if (canSeeCore) {
      list.push(TABS.FILES, TABS.INJURY, TABS.BIOMARKERS);
    }
    if (canSeeNotes) {
      list.push(TABS.NOTES);
    }
    return list;
  }, [canSeeCore, canSeeNotes]);

  // Si l’onglet actif n’est pas autorisé (ou permissions viennent de changer), on sélectionne le premier autorisé
  useEffect(() => {
    if (!loadingPerms) {
      if (!allowedTabs.includes(activeTab)) {
        setActiveTab(allowedTabs[0] || null);
      }
    }
  }, [loadingPerms, allowedTabs, activeTab]);

  // États de chargement / erreurs simples
  if (loadingPerms || loadingPlayer) return <p className="p-6">Loading…</p>;
  if (!player) return <p className="p-6">Player not found.</p>;
  if (allowedTabs.length === 0) {
    return (
      <div className={`p-6 min-h-screen ${isDarkMode ? "bg-gray-950 text-gray-100" : "bg-gray-50 text-gray-900"}`}>
        <h1 className="text-2xl font-semibold mb-2">Insufficient permissions</h1>
        <p className={isDarkMode ? "text-gray-400" : "text-gray-600"}>
          You don’t have permission to view this page.
        </p>
      </div>
    );
  }

  return (
    <div className={`p-6 space-y-8 min-h-screen ${isDarkMode ? "bg-gray-950 text-gray-100" : "bg-gray-50 text-gray-900"}`}>
      <header className="space-y-2">
        <h1 className="text-3xl font-bold">Update info – {player.first_name} {player.last_name}</h1>
        <p className={isDarkMode ? "text-gray-400" : "text-gray-600"}>Manage files, injuries, physio notes, and biomarkers.</p>
      </header>

      <nav className="flex flex-wrap gap-2">
        {canSeeCore && (
          <>
            <TabButton active={activeTab === TABS.FILES} onClick={() => setActiveTab(TABS.FILES)}>
              Imagery
            </TabButton>
            <TabButton active={activeTab === TABS.INJURY} onClick={() => setActiveTab(TABS.INJURY)}>
              Report an injury
            </TabButton>
            <TabButton active={activeTab === TABS.BIOMARKERS} onClick={() => setActiveTab(TABS.BIOMARKERS)}>
              Update Biomarkers
            </TabButton>
          </>
        )}

        {canSeeNotes && (
          <TabButton active={activeTab === TABS.NOTES} onClick={() => setActiveTab(TABS.NOTES)}>
            Physio notes
          </TabButton>
        )}
      </nav>

      {/* Contenu des onglets – affiché seulement si autorisé */}
      {activeTab === TABS.FILES       && canSeeCore  && <ImageryTab     playerId={playerId} />}
      {activeTab === TABS.INJURY      && canSeeCore  && <InjuryTab      playerId={playerId} />}
      {activeTab === TABS.BIOMARKERS  && canSeeCore  && <BiomarkersTab  playerId={playerId} />}
      {activeTab === TABS.NOTES       && canSeeNotes && <NotesTab       playerId={playerId} />}
    </div>
  );
}
